import * as React from 'react';
import { useContext } from 'react';

import type { ApolloError } from '@apollo/client';

import type { EnterpriseLoggedInPageHeader_MyPageHeaderFragment as MyLoggedInPageHeader } from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedInMyPageHeaderQuery';
import type { EnterpriseLoggedOutPageHeader_MyPageHeaderFragment as MyLoggedOutPageHeader } from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedOutMyPageHeaderQuery';

export type EnterpriseMyPageHeaderContextType = {
  myPageHeader?: MyLoggedInPageHeader | MyLoggedOutPageHeader;
  hasEnrolledPrograms?: boolean;
  // To be used to indicate to page header renderers that we may want to render a fallback header as the call to get the header data failed if this is defined.
  error?: ApolloError;
};

const EnterpriseMyPageHeaderContext = React.createContext<EnterpriseMyPageHeaderContextType | undefined>(undefined);

// Type guard to check if myPageHeader is MyLoggedInPageHeader
const isLoggedInPageHeader = (
  myPageHeader: MyLoggedInPageHeader | MyLoggedOutPageHeader | undefined
): myPageHeader is MyLoggedInPageHeader => {
  if (!myPageHeader) {
    return false;
  }

  const loggedInPageHeaderKeys = [
    'enrolledDegreePrograms',
    'enrolledEnterprisePrograms',
    'naptimeOrganizationsWithProgramEnrollments',
    'naptimeChosenOrganization',
    'chosenEnterpriseProgram',
    'chosenDegree',
    'hasCheatingIncidents',
    'canViewEducatorAdminLink',
    'isCourseraPlusLearner',
    'courseraPlusSubscriptionCycle',
    'naptimeManagedThirdPartyOrganizations',
  ];

  for (const headerKey in myPageHeader) {
    if (loggedInPageHeaderKeys.includes(headerKey)) {
      return true;
    }
  }

  return false;
};

// Created to ensure the page header BFF and the backup made via old queries are consistent
export type UseEnterpriseMyPageHeaderResult = {
  enrolledDegreePrograms?: MyLoggedInPageHeader['enrolledDegreePrograms'];
  enrolledEnterprisePrograms?: MyLoggedInPageHeader['enrolledEnterprisePrograms'];
  naptimeOrganizationsWithProgramEnrollments?: MyLoggedInPageHeader['naptimeOrganizationsWithProgramEnrollments'];
  currentPageProgram?: MyLoggedInPageHeader['currentPageProgram'] | MyLoggedOutPageHeader['currentPageProgram'];
  naptimeCurrentPageOrganization?:
    | MyLoggedInPageHeader['naptimeCurrentPageOrganization']
    | MyLoggedOutPageHeader['naptimeCurrentPageOrganization'];
  chosenEnterpriseProgram?: MyLoggedInPageHeader['chosenEnterpriseProgram'];
  naptimeChosenOrganization?: MyLoggedInPageHeader['naptimeChosenOrganization'];
  chosenDegree?: MyLoggedInPageHeader['chosenDegree'];
  hasCheatingIncidents?: MyLoggedInPageHeader['hasCheatingIncidents'];
  canViewEducatorAdminLink?: MyLoggedInPageHeader['canViewEducatorAdminLink'];
  isCourseraPlusLearner?: MyLoggedInPageHeader['isCourseraPlusLearner'];
  courseraPlusSubscriptionCycle?: MyLoggedInPageHeader['courseraPlusSubscriptionCycle'];
  naptimeManagedThirdPartyOrganizations?: MyLoggedInPageHeader['naptimeManagedThirdPartyOrganizations'];
  hasEnrolledPrograms?: boolean | null;
};

export const useEnterpriseMyPageHeader = (): UseEnterpriseMyPageHeaderResult => {
  const context = useContext(EnterpriseMyPageHeaderContext);

  if (!context) {
    throw new Error('useEnterpriseMyPageHeader must be used within an EnterpriseMyPageHeaderContext.Provider');
  }

  const { myPageHeader } = context;
  const isLoggedIn = isLoggedInPageHeader(myPageHeader);
  const currentPageProgram = myPageHeader?.currentPageProgram;
  const naptimeCurrentPageOrganization = myPageHeader?.naptimeCurrentPageOrganization;

  // Only access these properties if we have a logged-in header
  const enrolledDegreePrograms = isLoggedIn ? myPageHeader.enrolledDegreePrograms : undefined;
  const enrolledEnterprisePrograms = isLoggedIn ? myPageHeader.enrolledEnterprisePrograms : undefined;
  const naptimeOrganizationsWithProgramEnrollments = isLoggedIn
    ? myPageHeader.naptimeOrganizationsWithProgramEnrollments
    : undefined;
  const hasEnrolledPrograms =
    (naptimeOrganizationsWithProgramEnrollments && naptimeOrganizationsWithProgramEnrollments.length > 0) ||
    (enrolledDegreePrograms && enrolledDegreePrograms.length > 0) ||
    (enrolledEnterprisePrograms && enrolledEnterprisePrograms.length > 0);

  return {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    naptimeOrganizationsWithProgramEnrollments: isLoggedIn
      ? myPageHeader.naptimeOrganizationsWithProgramEnrollments
      : undefined,
    currentPageProgram,
    naptimeCurrentPageOrganization,
    chosenEnterpriseProgram: isLoggedIn ? myPageHeader.chosenEnterpriseProgram : undefined,
    naptimeChosenOrganization: isLoggedIn ? myPageHeader.naptimeChosenOrganization : undefined,
    chosenDegree: isLoggedIn ? myPageHeader.chosenDegree : undefined,
    hasCheatingIncidents: isLoggedIn ? myPageHeader.hasCheatingIncidents : undefined,
    canViewEducatorAdminLink: isLoggedIn ? myPageHeader.canViewEducatorAdminLink : undefined,
    isCourseraPlusLearner: isLoggedIn ? myPageHeader.isCourseraPlusLearner : undefined,
    courseraPlusSubscriptionCycle: isLoggedIn ? myPageHeader.courseraPlusSubscriptionCycle : undefined,
    naptimeManagedThirdPartyOrganizations: isLoggedIn ? myPageHeader.naptimeManagedThirdPartyOrganizations : undefined,
    hasEnrolledPrograms,
  };
};

export const useEnterpriseMyPageHeaderError = () => {
  const context = useContext(EnterpriseMyPageHeaderContext);

  return context?.error;
};

export default EnterpriseMyPageHeaderContext;
